import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import {
  Button,
  Balance,
  Table,
  TransHeader,
  Wrapper,
  TransFooter,
  Pagination,
  Select,
  // Input,
  TableWrapper,
  EmptyState,
  SelectWrapper,
  StatusBadge,
  DateRangeWrapper,
  WrapperInner,
  // DatesContainer,
} from './styles';
import {
  fetchAuthorizations,
  fetchTransactions,
} from '../../../../../services';
import DateRangePicker from '../../../../../components/DateRangePicker';
import CardVector from '../../../../../components/CardVector';

function Transactions(props) {
  const intl = useIntl();
  const { prn, setprn, balanceInfo, setisprn, theme, page } = props;

  const convertToDate = dateStr => {
    const date = new Date(dateStr);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const [transactions, setTransactions] = useState([]);
  const [loading, setloading] = useState(false);
  const [error, seterror] = useState(null);
  const [pagination, setpagination] = useState(() => {
    return {
      page: 1,
      limit: 10,
      startDate: null, // 1 year ago
      endDate: null, // Today, formatted
    };
  });

  const { limit, startDate, endDate } = pagination;

  const formatDate = useMemo(() => {
    return date => {
      const d = new Date(date);
      const day = d
        .getDate()
        .toString()
        .padStart(2, '0');
      const month = d.toLocaleString('en-US', { month: 'short' });
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };
  }, []);

  // const formatCurrency = useMemo(() => {
  //   const formatter = new Intl.NumberFormat('en-US', {
  //     style: 'currency',
  //     currency: 'USD',
  //     minimumFractionDigits: 2,
  //   });

  //   return amount => formatter.format(amount);
  // }, []);

  const isTransactionsEmpty = !transactions || !transactions.length;

  // fetch transactions
  const getTransactions = async (
    pagenumber = 1,
    pagelimit = 10,
    startDate = '',
    endDate = ''
  ) => {
    setloading(true);
    seterror(null);
    const now = new Date();
    const sdate = startDate
      ? convertToDate(startDate)
      : convertToDate(now.setFullYear(now.getFullYear() - 3));
    const edate = endDate ? convertToDate(endDate) : convertToDate(new Date());
    try {
      const res = await fetchTransactions(
        prn,
        pagenumber,
        pagelimit,
        sdate,
        edate
      );
      const json = await res.json();

      if (json.error) throw new Error(json.error.message);

      const settled = json.data.map(transaction => ({
        ...transaction,
        status: 'settled',
      }));

      let pending = [];

      if (pagenumber === 1) {
        const authRes = await fetchAuthorizations(prn);
        const authJson = await authRes.json();

        if (authJson.error) throw new Error(authJson.error.message);

        pending = authJson.data.map(authorization => ({
          ...authorization,
          status: 'pending',
        }));

        const parseDMYToUTC = dateStr => {
          const [day, month, year] = dateStr.split('-').map(Number);
          return new Date(Date.UTC(year, month - 1, day));
        };

        const start = parseDMYToUTC(sdate);
        const end = parseDMYToUTC(edate);
        end.setUTCHours(23, 59, 59, 999);

        pending = pending.filter(transaction => {
          const txTime = new Date(transaction.timestamp);
          return txTime >= start && txTime <= end;
        });

        // Sort by timestamp descending
        pending.sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      }

      setTransactions(pagenumber === 1 ? [...pending, ...settled] : settled);

      setpagination({
        page: json.page,
        limit: json.limit,
        startDate: json.start_date,
        endDate: json.end_date,
      });
    } catch (error) {
      seterror(error.message);
    }
    setloading(false);
  };

  const handleDateRange = range => {
    const sdate = range.start ? range.start : null;
    const edate = range.end ? range.end : null;
    getTransactions(1, pagination.limit, sdate, edate);
  };

  const handlePageChange = page => {
    setpagination(prev => {
      return {
        ...prev,
        page,
      };
    });
    getTransactions(
      page,
      pagination.limit,
      pagination.startDate,
      pagination.endDate
    );
  };

  const handleLimitChange = e => {
    const limit = parseInt(e.target.value, 10);
    setpagination(prev => {
      return {
        ...prev,
        limit,
      };
    });
    getTransactions(
      pagination.page,
      limit,
      pagination.startDate,
      pagination.endDate
    );
  };

  useEffect(() => {
    getTransactions();
  }, [prn]);

  const renderType = transaction => {
    return transaction.description === 'Payment'
      ? 'Cash Top Up'
      : 'Reverse Value Load';
  };

  const formatCurrency = useMemo(() => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    });

    return amount => formatter.format(amount);
  }, []);

  return (
    <Wrapper
      className={`${
        Object.values(theme.brand.cbc_footer || {}).filter(l => l.url).length >
        0
          ? 'hasfooterlinks'
          : ''
      }`}
    >
      <WrapperInner>
        <TransHeader>
          <Balance>
            <p>{intl.messages[`${page}-available_balance`]}</p>
            <h2>{formatCurrency(balanceInfo.balance)}</h2>
          </Balance>
          <Button
            type="button"
            onClick={() => {
              setprn('');
              setisprn(false);
            }}
            title="Go to main page"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.375 9.375H16.875C16.9571 9.375 17.0383 9.39117 17.1142 9.42258C17.19 9.45398 17.2589 9.50002 17.3169 9.55806C17.375 9.61609 17.421 9.68499 17.4524 9.76082C17.4838 9.83665 17.5 9.91792 17.5 10C17.5 10.0821 17.4838 10.1633 17.4524 10.2392C17.421 10.315 17.375 10.3839 17.3169 10.4419C17.2589 10.5 17.19 10.546 17.1142 10.5774C17.0383 10.6088 16.9571 10.625 16.875 10.625H4.375C4.20924 10.625 4.05027 10.5592 3.93306 10.4419C3.81585 10.3247 3.75 10.1658 3.75 10C3.75 9.83424 3.81585 9.67527 3.93306 9.55806C4.05027 9.44085 4.20924 9.375 4.375 9.375Z"
                fill="#212121"
              />
              <path
                d="M4.63345 9.99997L9.81705 15.1823C9.93437 15.2996 10.0003 15.4587 10.0003 15.6246C10.0003 15.7906 9.93437 15.9497 9.81704 16.067C9.69972 16.1844 9.54059 16.2503 9.37466 16.2503C9.20874 16.2503 9.04961 16.1844 8.93228 16.067L3.30728 10.442C3.24911 10.384 3.20296 10.315 3.17147 10.2391C3.13999 10.1632 3.12378 10.0818 3.12378 9.99965C3.12378 9.91747 3.13999 9.83609 3.17147 9.76018C3.20296 9.68427 3.24911 9.61532 3.30728 9.55726L8.93228 3.93226C8.99037 3.87417 9.05934 3.82809 9.13525 3.79665C9.21115 3.76521 9.2925 3.74902 9.37466 3.74902C9.45682 3.74902 9.53817 3.76521 9.61408 3.79665C9.68998 3.82809 9.75895 3.87417 9.81705 3.93226C9.87514 3.99036 9.92122 4.05933 9.95266 4.13523C9.9841 4.21114 10.0003 4.29249 10.0003 4.37465C10.0003 4.45681 9.9841 4.53816 9.95266 4.61406C9.92122 4.68997 9.87514 4.75894 9.81705 4.81703L4.63345 9.99997Z"
                fill="#212121"
              />
            </svg>
            {intl.messages[`${page}-back_title`]}
          </Button>
        </TransHeader>
        <TransHeader>
          <h4>{intl.messages[`${page}-transaction_title`]}</h4>
          <TransFooter>
            <DateRangeWrapper>
              <DateRangePicker
                onRangeSelect={handleDateRange}
                dateRange={{ start: startDate, end: endDate }}
              />
            </DateRangeWrapper>
          </TransFooter>
        </TransHeader>
        {!isTransactionsEmpty && !loading ? (
          <>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>{intl.messages[`${page}-date`]}</th>
                    <th>{intl.messages[`${page}-transaction_type`]}</th>
                    <th>{intl.messages[`${page}-status`]}</th>
                    <th>{intl.messages[`${page}-merchant_info`]}</th>
                    <th>{intl.messages[`${page}-amount`]}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(transaction => {
                    const isCredit = transaction.billing_amount > 0;
                    return (
                      <tr key={`transaction${transaction.timestamp}`}>
                        <td>{formatDate(transaction.timestamp)}</td>
                        <td>{transaction.description}</td>
                        <td>
                          <StatusBadge
                            className={`status_badge ${transaction.status}`}
                          >
                            {transaction.status}
                          </StatusBadge>
                        </td>
                        <td>
                          <p>
                            {transaction.details ===
                            'createAccountTransfer API call'
                              ? renderType(transaction)
                              : transaction.details ||
                                transaction.merchant.name ||
                                'N/A'}
                          </p>
                        </td>
                        <td className={isCredit ? 'credit' : 'debit'}>
                          {isCredit ? '+' : ''}
                          {formatCurrency(transaction.billing_amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
            <TransFooter>
              <SelectWrapper>
                <Select
                  value={limit}
                  title="Page Size"
                  onChange={handleLimitChange}
                >
                  <option>10</option>
                  <option>20</option>
                  <option>50</option>
                  <option>100</option>
                </Select>
              </SelectWrapper>
              <Pagination>
                <Button
                  type="button"
                  title="Go to previous page"
                  disabled={pagination.page === 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  {intl.messages[`${page}-previous`]}
                </Button>
                <p title="Current page">{pagination.page}</p>
                <Button
                  type="button"
                  title="Go to next page"
                  disabled={transactions.length < limit}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  {intl.messages[`${page}-next`]}
                </Button>
              </Pagination>
            </TransFooter>
          </>
        ) : (
          <>
            <EmptyState>
              <CardVector color={theme.brand.primary} isEmpty />
              <p>
                {loading && (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 1.44 1.44"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fill="#fff" fillOpacity=".01" d="M0 0h1.44v1.44H0z" />
                    <path
                      d="M.12.72a.6.6 0 0 0 .6.6v0a.6.6 0 0 0 0-1.2"
                      stroke="#212121"
                      strokeWidth=".12"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1.08.72a.36.36 0 1 0-.36.36v0"
                      stroke="#212121"
                      strokeWidth=".12"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {loading && intl.messages[`${page}-transaction-loading`]}
                {!loading &&
                  (error ||
                    intl.messages[
                      `${page}-transaction-empty-${
                        startDate ? 'filter' : 'default'
                      }`
                    ])}
              </p>
            </EmptyState>
          </>
        )}
      </WrapperInner>
    </Wrapper>
  );
}

Transactions.propTypes = {
  page: PropTypes.string.isRequired,
  balanceInfo: PropTypes.shape({
    balance: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }),
  prn: PropTypes.string.isRequired,
  setprn: PropTypes.func.isRequired,
  setisprn: PropTypes.func.isRequired,
  theme: PropTypes.shape({
    brand: PropTypes.shape({
      primary: PropTypes.string,
      cbc_footer: PropTypes.shape({}),
    }),
  }),
};

Transactions.defaultProps = {
  theme: {},
  balanceInfo: {
    balance: '',
    currency: '',
  },
};

export default withTheme(Transactions);
